

import type { TCalendarView } from '../types'

import type { ICalendarEvent } from '@/features/calendar/interfaces'
import { useMemo, useState } from 'react'
import { CalendarContext } from './create-calendar-context'
import { useAuth } from '@/features/authentication/components/auth-provider'

export interface ICalendarContext {
  selectedDate: Date
  setSelectedDate: (date: Date | undefined) => void
  selectedTrainingId: ICalendarEvent['id'] | ''
  setSelectedTrainingId: (trainingId: ICalendarEvent['id'] | '') => void
  badgeVariant: 'dot' | 'colored'
  setBadgeVariant: (variant: 'dot' | 'colored') => void
  trainings: ICalendarEvent[]
  // bookings: IBooking[]
  view: TCalendarView
  setView: (view: TCalendarView) => void
  canManageEvents: boolean
  currentMember?: CalendarMemberProfile
}

export type CalendarMemberProfile = {
  name?: string
  email?: string
  phone?: string
}

interface CalendarProviderProps {
  children: React.ReactNode
  trainings: ICalendarEvent[]
}

export function CalendarProvider({ children, trainings }: CalendarProviderProps) {
  const { user } = useAuth()

  const canManageEvents = user?.roles?.some(role => role === 'ROLE_ADMIN') ?? false

  const currentMember = useMemo<CalendarMemberProfile | undefined>(() => {
    if (!user) return undefined

    const nameParts = [user.firstName, user.lastName].filter(part => part && part.trim())
    const derivedName = nameParts.length ? nameParts.join(' ') : user.name
    const fallbackEmail = (user.email || user.username || '').trim() || undefined
    const fallbackName = (derivedName || fallbackEmail || user.username || '').trim() || undefined

    return {
      name: fallbackName,
      email: fallbackEmail,
      phone: user.phoneNumber ?? undefined,
    }
  }, [user])

  const [badgeVariant, setBadgeVariant] = useState<'dot' | 'colored'>('dot')

  const [selectedDate, setSelectedDate] = useState(() => new Date())
  // Default to 'all' so all trainings are visible initially
  const [selectedTrainingId, setSelectedTrainingId] = useState<ICalendarEvent['id'] | 'all'>('all')
  const [view, setView] = useState<TCalendarView>('month')

  const handleSelectDate = (date: Date | undefined) => {
    if (!date)
      return
    setSelectedDate(date)
  }

  const contextValue: ICalendarContext = {
    selectedDate,
    setSelectedDate: handleSelectDate,
    selectedTrainingId,
    setSelectedTrainingId,
    badgeVariant,
    setBadgeVariant,
    trainings,
    // bookings,
    view,
    setView,
    canManageEvents,
    currentMember,
  }

  return (
    <CalendarContext.Provider
      value={contextValue}
    >
      {children}
    </CalendarContext.Provider>
  )
}
